import {
  Button,
  Grid,
  Paper,
  Typography,
  Stack,
  Checkbox,
  FormControlLabel,
  Alert,
} from "@mui/material";
import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  setApplicationStep,
  setUkRestrictedStatement,
  useOnboarding,
} from "src/store/features/onboarding.slice";
import api from "src/store/features/api";
import UKAppropriatenessWarningDialog from "./UKAppropriatenessWarningDialog";

interface UKRestrictedInvestorStatementProps {
  // When provided, the component runs in public (token-only) mode: it skips the
  // authenticated API call and Redux step dispatch and defers navigation to the
  // parent instead.
  onPublicProceed?: () => void;
  onPublicBack?: () => void;
}

export default function UKRestrictedInvestorStatement({
  onPublicProceed,
  onPublicBack,
}: UKRestrictedInvestorStatementProps = {}) {
  const dispatch = useDispatch();
  const { ukOnboarding } = useOnboarding();

  const [declarationAgreed, setDeclarationAgreed] = useState(
    ukOnboarding.restrictedStatement.declarationAgreed,
  );
  const [error, setError] = useState<string | null>(null);
  const [warningOpen, setWarningOpen] = useState(false);

  const [submitRestrictedStatement, { isLoading }] = api.useSubmitRestrictedInvestorStatementMutation();

  const handleContinue = () => {
    if (!declarationAgreed) {
      setError("You must agree to the declaration to continue.");
      return;
    }
    setError(null);
    setWarningOpen(true);
  };

  const handleProceed = async () => {
    if (onPublicProceed) {
      setWarningOpen(false);
      onPublicProceed();
      return;
    }
    try {
      await submitRestrictedStatement({
        submitRestrictedInvestorStatementDto: { declarationAgreed: true },
      }).unwrap();

      dispatch(
        setUkRestrictedStatement({
          declarationAgreed: true,
        }),
      );
      dispatch(setApplicationStep("ukAssessment"));
    } catch (err: any) {
      setWarningOpen(false);
      setError(err?.data?.message || "Failed to submit your statement. Please try again.");
    }
  };

  const handleBack = () => {
    if (onPublicBack) {
      onPublicBack();
      return;
    }
    dispatch(setApplicationStep("ukInvestorType"));
  };

  return (
    <Grid container spacing={3} mt={2}>
      <Grid item xs={12}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Restricted Investor Statement
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={3}>
          As a Restricted Investor, you are limited to investing no more than 10% of your net
          assets in high-risk investments.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Restricted Investor Declaration
          </Typography>
          <Typography variant="body1" paragraph>
            I declare that I qualify as a Restricted Investor because, in the last twelve
            months, I have not invested more than 10% of my net assets in high-risk
            investments.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>Net assets</strong> means total assets minus total liabilities,
            excluding:
          </Typography>
          <Stack spacing={1} ml={2} mb={2}>
            <Typography variant="body2" color="text.secondary">
              • The value of your primary residence
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Any pension funds
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Any life insurance policies
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>High-risk investments</strong> include unlisted shares, peer-to-peer
            loans, and cryptoassets.
          </Typography>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            backgroundColor: "#FFF8E1",
            border: "1px solid #FFE082",
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" paragraph>
            <strong>Important:</strong> By making this declaration, you confirm that:
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2">
              1. You have not invested more than 10% of your net assets in high-risk
              investments in the past 12 months.
            </Typography>
            <Typography variant="body2">
              2. You will not invest more than 10% of your net assets in cryptoassets
              through this platform.
            </Typography>
            <Typography variant="body2">
              3. Being a restricted investor will expose me to promotions for investment where
              there is a risk of losing all the money I invest. I am aware that it is open to
              me to seek professional advice before making any investment in a high-risk
              investment.
            </Typography>
            <Typography variant="body2">
              4. Putting all your money into a single business or type of investment is
              risky. Spreading your money across different investments makes you less
              dependent on any one to do well.
            </Typography>
            <Typography variant="body2">
              5. You understand that this is a self-certification and providing false
              information may have legal consequences.
            </Typography>
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={declarationAgreed}
              onChange={(e) => setDeclarationAgreed(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              I confirm that I have read and understood the above declaration, and that I
              qualify as a Restricted Investor as defined by the FCA.
            </Typography>
          }
        />
      </Grid>

      {error && (
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      )}


      <Grid item xs={12} sx={{ mb: { xs: 3, sm: 4 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          mt={2}
        >
          <Button variant="outlined" color="inherit" size="large" onClick={handleBack}>
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleContinue}
            disabled={!declarationAgreed || isLoading}
            sx={{ minWidth: 200 }}
          >
            {isLoading ? "Submitting..." : "Continue"}
          </Button>
        </Stack>
      </Grid>

      <UKAppropriatenessWarningDialog
        open={warningOpen}
        isLoading={isLoading}
        investorType="restricted"
        onClose={() => setWarningOpen(false)}
        onProceed={handleProceed}
        onExit={() => {
          setWarningOpen(false);
          handleBack();
        }}
      />
    </Grid>
  );
}
